import { useEffect, useCallback, useRef } from 'react';

export interface DismissibleContentProps {
  isOpen: boolean;
  toggle: () => void;
  children: React.ReactNode;
  refElement?: keyof React.ReactHTML | React.ElementType;
  disableClickOutside?: boolean;
  disableKeyPress?: boolean;
  disableFocus?: boolean;
  disableScroll?: boolean;
  ignoreElementRef?: HTMLButtonElement;
}

const focusableElementsQuery =
  'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, *[tabindex], *[contenteditable]';

export const DismissibleContent = ({
  isOpen,
  toggle,
  refElement: RefElement = 'span',
  children,
  disableClickOutside = false,
  disableKeyPress = false,
  disableFocus = false,
  disableScroll = false,
  ignoreElementRef,
}: DismissibleContentProps) => {
  const ref = useRef<HTMLSpanElement>();

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (!isOpen || !ref.current) {
        return;
      }

      if (
        !ref.current.contains(event.target as Node) &&
        (!ignoreElementRef || !ignoreElementRef.contains(event.target as Node))
      ) {
        toggle();
      }
    },
    [isOpen, toggle, ignoreElementRef]
  );

  const handleKeyPress = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        toggle();
      }
    },
    [isOpen, toggle]
  );

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const firstFocusableElement = ref.current.querySelector(
      focusableElementsQuery
    ) as HTMLElement;

    if (firstFocusableElement && !disableFocus && isOpen) {
      setTimeout(() =>
        firstFocusableElement.focus({ preventScroll: disableScroll })
      );
    }
  }, [ref, isOpen, disableFocus, disableScroll]);

  useEffect(() => {
    if (!disableKeyPress) {
      document.addEventListener('keyup', handleKeyPress);
    }
    if (!disableClickOutside) {
      document.addEventListener('click', handleClickOutside, { capture: true });
    }
    return () => {
      if (!disableKeyPress) {
        document.removeEventListener('keyup', handleKeyPress);
      }
      if (!disableClickOutside) {
        document.removeEventListener('click', handleClickOutside, {
          capture: true,
        });
      }
    };
  }, [
    handleClickOutside,
    handleKeyPress,
    disableKeyPress,
    disableClickOutside,
  ]);

  return <RefElement ref={ref}>{children}</RefElement>;
};
